import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../lib/apiClient';
import { useToast } from '../../lib/useToast';
import { useAuth } from '../auth/useAuth';
import { claimApi } from './claim.api';

const tijd = (iso: string) =>
  new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });

/**
 * "Ik ga vandaag boodschappen doen."
 *
 * Het probleem dat dit oplost is niet de lijst maar de rit: zonder dit rijden
 * er twee mensen naar dezelfde winkel. De claim vervalt vanzelf aan het eind
 * van de dag, zodat een vergeten claim de volgende ochtend niemand blokkeert.
 */
export function ClaimBalk() {
  const { user } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();

  const claim = useQuery({
    queryKey: ['claim'],
    queryFn: claimApi.huidig,
    // De claim verandert buiten je om — iemand anders pakt hem op. Vaker
    // verversen dan de lijst, want hierop baseer je of je zelf gaat.
    refetchInterval: 60_000,
  });

  const ververs = () => void qc.invalidateQueries({ queryKey: ['claim'] });

  const pakOp = useMutation({
    mutationFn: claimApi.claim,
    onSuccess: () => {
      ververs();
      toast('Je staat genoteerd voor vandaag');
    },
    onError: (err) => {
      ververs();
      toast(err instanceof ApiError ? err.message : 'Er ging iets mis', true);
    },
  });

  const geefVrij = useMutation({
    mutationFn: claimApi.vrijgeven,
    onSuccess: () => {
      ververs();
      toast('Weer vrijgegeven');
    },
    onError: () => toast('Vrijgeven mislukt', true),
  });

  const huidig = claim.data?.claim ?? null;
  const bezig = pakOp.isPending || geefVrij.isPending;

  if (claim.isPending) return null;

  if (!huidig) {
    return (
      <div className="vs-claim">
        <span className="vs-claim__tekst">Niemand gaat vandaag boodschappen doen.</span>
        <button className="vs-btn vs-btn--sm" onClick={() => pakOp.mutate()} disabled={bezig}>
          Ik ga vandaag
        </button>
      </div>
    );
  }

  const ikZelf = huidig.door === user?.naam;
  return (
    <div className={`vs-claim${ikZelf ? ' vs-claim--ik' : ''}`}>
      <span className="vs-claim__tekst">
        {ikZelf ? (
          <>
            <strong>Jij</strong> gaat vandaag boodschappen doen.
          </>
        ) : (
          <>
            <strong>{huidig.door}</strong> gaat vandaag boodschappen doen.
          </>
        )}{' '}
        <span className="vs-claim__tijd">vervalt om {tijd(huidig.vervaltOp)}</span>
      </span>
      {ikZelf && (
        <button
          className="vs-btn vs-btn--ghost vs-btn--sm"
          onClick={() => geefVrij.mutate()}
          disabled={bezig}
        >
          Toch niet
        </button>
      )}
    </div>
  );
}
